import { createPublicClient, http, type PublicClient } from 'viem';
import { validateRecoveryRpcUrl } from './walletSafety.js';

const methods = new Set(['eth_chainId', 'eth_getBlockByNumber', 'eth_getCode', 'eth_getStorageAt', 'eth_getTransactionCount', 'eth_getBalance', 'eth_call', 'eth_estimateGas', 'eth_getTransactionByHash', 'eth_getTransactionReceipt']);
/** Selected RPC only, read-only methods, bounded decoded body, no redirects/retry. */
export function boundedRecoveryFetch(fetcher: typeof fetch = fetch): typeof fetch {
  return async (url, init) => {
    validateRecoveryRpcUrl(url instanceof Request ? url.url : String(url));
    if (typeof init?.body !== 'string') throw new Error('Recovery RPC requires a single JSON request');
    let request: { jsonrpc?: unknown; id?: unknown; method?: unknown; params?: unknown };
    try { request = JSON.parse(init.body); } catch { throw new Error('Unsupported recovery RPC request envelope'); }
    const validId = (id: unknown) => typeof id === 'number' && Number.isSafeInteger(id) && id >= 0 || typeof id === 'string' && id.length > 0 && id.length <= 128;
    if (!request || typeof request !== 'object' || Array.isArray(request) || request.jsonrpc !== '2.0' || !validId(request.id)
      || typeof request.method !== 'string' || !methods.has(request.method)
      || !(Array.isArray(request.params) || request.method === 'eth_chainId' && request.params === undefined)
      || Object.keys(request).some(key => !['jsonrpc', 'id', 'method', 'params'].includes(key))) throw new Error('Unsupported recovery RPC request envelope or method');
    const timeout = AbortSignal.timeout(10000);
    const signal = init.signal ? AbortSignal.any([init.signal, timeout]) : timeout;
    const response = await fetcher(url, { ...init, signal, redirect: 'error', credentials: 'omit', referrerPolicy: 'no-referrer' });
    if (!response.ok || !response.body || response.redirected) { await response.body?.cancel().catch(() => undefined); throw new Error('Recovery RPC response unavailable or redirected'); }
    const reader = response.body.getReader(), chunks: Uint8Array[] = [];
    let size = 0;
    try {
      for (;;) {
        const result = await reader.read(); if (result.done) break;
        size += result.value.byteLength;
        if (size > 2 * 1024 * 1024 || signal.aborted) throw new Error('Recovery RPC response too large or timed out');
        chunks.push(result.value);
      }
    } catch (error) { await reader.cancel().catch(() => undefined); throw error; }
    finally { reader.releaseLock(); }
    const bytes = new Uint8Array(size); let offset = 0;
    for (const chunk of chunks) { bytes.set(chunk, offset); offset += chunk.length; }
    let envelope: { jsonrpc?: unknown; id?: unknown; result?: unknown; error?: unknown };
    try { envelope = JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(bytes)); }
    catch { throw new Error('Invalid recovery RPC response envelope'); }
    if (!envelope || typeof envelope !== 'object' || Array.isArray(envelope) || envelope.jsonrpc !== '2.0' || envelope.id !== request.id
      || Object.keys(envelope).some(key => !['jsonrpc', 'id', 'result', 'error'].includes(key))
      || Object.hasOwn(envelope, 'result') === Object.hasOwn(envelope, 'error')) throw new Error('Mismatched or ambiguous recovery RPC response envelope');
    if (Object.hasOwn(envelope, 'error')) {
      const error = envelope.error;
      if (!error || typeof error !== 'object' || Array.isArray(error) || !('code' in error) || !Number.isSafeInteger(error.code)
        || !('message' in error) || typeof error.message !== 'string' || Object.keys(error).some(key => !['code', 'message', 'data'].includes(key))) throw new Error('Invalid recovery RPC error envelope');
      // Preserve structured RPC errors for viem; never throw/log provider text here.
    }
    return new Response(bytes, { status: response.status, headers: { 'content-type': 'application/json' } });
  };
}
export function createRecoveryClient(url: string): PublicClient {
  return createPublicClient({ ccipRead: false, cacheTime: 0, transport: http(url, { retryCount: 0, timeout: 10000, fetchFn: boundedRecoveryFetch() }) });
}
